import { computed, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ImportService } from '../../Modules/Renewal_Manager/Services/import.service';
import {
  IAccessTokenResponse,
  ILoginResponse,
  ILogoutResponse,
  User,
} from '../Interface/auth.interface';
import { HttpService } from './http.service';
import { StorageCryptoService } from './storage-crypto.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api = inject(HttpService);
  private importService = inject(ImportService);
  private storage = inject(StorageCryptoService);

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'user_data';
  accessTokenSignal = signal<string | null>(this.getAccessToken());
  // Derived signal: automatically updates when accessToken changes
  isAuthenticated = computed(() => !!this.accessTokenSignal());

  private setLastUploadedDateSource = new BehaviorSubject<string>(
    (localStorage.getItem('dataUploadedDate') ?? '').toString()
  );
  getLastUploadedDate: Observable<string> = this.setLastUploadedDateSource.asObservable();

  private setImportedFilenameSource = new BehaviorSubject<string>(
    localStorage.getItem('imported_filename') ?? ''
  );
  getImportedFilename$: Observable<string> = this.setImportedFilenameSource.asObservable();

  /**
   * Login API
   * @param credentials LoginResponse
   * @returns
   */
  login(credentials: any) {
    return this.api.post<ILoginResponse>('auth/login', credentials).pipe(
      tap((response: ILoginResponse) => {
        this.saveAuthData(response?.data);
        return response;
      })
    );
  }

  /**
   * To store token in local storage
   * @param res
   */
  saveAuthData(data: { access_token: string; refresh_token?: string; user?: User }): void {
    if (data.access_token) {
      this.storage.setItem(this.ACCESS_TOKEN_KEY, data.access_token);
      this.accessTokenSignal.set(data.access_token);
    }

    if (data.refresh_token) {
      this.storage.setItem(this.REFRESH_TOKEN_KEY, data.refresh_token);
    }

    if (data.user) {
      this.storage.setItem(this.USER_KEY, data.user);
      if(data.user?.data_imported_at){
        this.setLastUploadedDate(data.user?.data_imported_at);
      } else {
        this.setLastUploadedDate('');
      }
      if(data.user?.imported_filename){
        this.setImportedFilename(data.user?.imported_filename);
      } else {
        this.setImportedFilename('');
      }
    }
  }

  /**
   * Set data last uploaded date to localstorage
   */
  setLastUploadedDate(newDate: string) {
    localStorage.setItem('dataUploadedDate', newDate);
    this.setLastUploadedDateSource.next(newDate);
  }

  /**
   * Persists the filename to localStorage and broadcasts the change
   */
  setImportedFilename(fileName: string): void {
    localStorage.setItem('imported_filename', fileName);
    this.setImportedFilenameSource.next(fileName);
  }

  /**
   * Logout API
   * @returns
   */
  logout() {
    this.clearAuth();
    // return this.api.post<ILogoutResponse>('auth/logout',{}).pipe(
    //   tap((response: ILogoutResponse) => {
    //     return response;
    //   })
    // );
  }

  /**
   * Clear the local storage and token
   */
  clearAuth() {
    this.storage.removeItem(this.ACCESS_TOKEN_KEY);
    this.storage.removeItem(this.REFRESH_TOKEN_KEY);
    this.storage.removeItem(this.USER_KEY);
    //Remove localstorage and set false for import data toast shown
    this.importService.toastShown.set(false);
    localStorage.removeItem('importDataToastShown');
    localStorage.removeItem('dataUploadedDate');
    localStorage.removeItem('imported_filename');
    localStorage.removeItem('my_app_date_range');
    this.accessTokenSignal.set(null);
  }

  /**
   * To get new access token
   * @returns access_token
   */
  refreshAccessToken(): Observable<string> {
    const refreshToken = this.getRefreshToken();

    // If no refresh token exists, immediately throw an error so the interceptor can logout
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    return this.api
      .post<IAccessTokenResponse>('auth/access-token', {
        refresh_token: refreshToken,
      })
      .pipe(
        map((response: IAccessTokenResponse) => {
          this.saveAuthData(response?.data);
          return response?.data?.access_token;
        })
      );
  }

  getAccessToken(): string | null {
    return this.storage.getItem<string>(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.storage.getItem<string>(this.REFRESH_TOKEN_KEY);
  }

  getUser<User>(): User | null {
    return this.storage.getItem<User>(this.USER_KEY);
  }
}
